import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  ACTION_TYPE_LABELS,
  communicationCoachService,
  StoredCommunication,
  ActionType,
} from '../services/communicationCoachService'
import { JobApplication } from './ApplicationWizard'
import { LoadingSpinner } from './LoadingSpinner'
import { MessageBox, useMessageBox } from './MessageBox'
import './StoredCommunicationsView.css'

interface StoredCommunicationsViewProps {
  application?: JobApplication
  onClose: () => void
  onViewCommunication?: (communication: StoredCommunication) => void
  onEditCommunication?: (communication: StoredCommunication) => void
  onDeleteCommunication?: (sessionId: string) => void
  refreshKey?: number
}

export const StoredCommunicationsView: React.FC<StoredCommunicationsViewProps> = ({
  application,
  onClose,
  onViewCommunication,
  onEditCommunication,
  onDeleteCommunication,
  refreshKey = 0,
}) => {
  const [communications, setCommunications] = useState<StoredCommunication[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterActionType, setFilterActionType] = useState<ActionType | ''>('')
  const { messageBox, showMessageBox } = useMessageBox()

  useEffect(() => {
    loadCommunications()
  }, [application?.id, filterActionType, refreshKey])

  const loadCommunications = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const stored = await communicationCoachService.getStoredCommunications(
        application?.id,
        filterActionType || undefined
      )
      setCommunications(stored)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stored communications')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const getActionTypeLabel = (type: ActionType): string => {
    return ACTION_TYPE_LABELS[type] || type
  }

  const getPreviewText = (communication: StoredCommunication): string => {
    const preview = communication.input_content?.trim() || ''
    if (communication.action_type === 'recruiter_outreach' || communication.action_type === 'cover_letter') {
      if (!preview) {
        return 'Using saved job post and selected/default resume for this application.'
      }
      return preview
    }
    return preview
  }

  const handleView = (communication: StoredCommunication) => {
    if (onViewCommunication) {
      onViewCommunication(communication)
    }
  }

  const handleEdit = (communication: StoredCommunication) => {
    if (onEditCommunication) {
      onEditCommunication(communication)
    }
  }

  const canEditCommunication = (communication: StoredCommunication) => (
    [
      'email_response',
      'recruiter_outreach',
      'cover_letter',
      'follow_up',
      'thank_you_note',
      'interview_confirmation',
      'offer_response',
    ].includes(communication.action_type)
  )

  const deleteCommunication = async (communication: StoredCommunication) => {
    try {
      await communicationCoachService.deleteStoredCommunication(communication.session_id)
      toast.success('Stored communication deleted')
      onDeleteCommunication?.(communication.session_id)
      await loadCommunications()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete stored communication')
    }
  }

  const handleDelete = (communication: StoredCommunication) => {
    showMessageBox({
      type: 'confirm',
      title: 'Delete Stored Communication',
      message: 'Are you sure you want to delete this stored communication?',
      description: 'This action cannot be undone.',
      buttons: ['agree', 'disagree'],
      buttonLabels: {
        agree: 'Delete',
        disagree: 'Cancel',
      },
      onButtonClick: (button) => {
        if (button === 'agree') {
          void deleteCommunication(communication)
        }
      },
    })
  }

  return (
    <div className="stored-communications-view">
      {!onViewCommunication && (
        <div className="stored-communications-header">
          <h2>Stored Communications</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>
      )}

      <div className="stored-communications-filters">
        <label>
          Filter by Type:
          <select
            value={filterActionType}
            onChange={(e) => setFilterActionType(e.target.value as ActionType | '')}
          >
            <option value="">All Types</option>
            <option value="email_response">Email Response</option>
            <option value="recruiter_outreach">Recruiter Outreach</option>
            <option value="cover_letter">Cover Letter</option>
            <option value="follow_up">Follow-Up</option>
            <option value="thank_you_note">Thank You Note</option>
            <option value="interview_confirmation">Interview Confirmation</option>
            <option value="offer_response">Offer Response</option>
            <option value="message_review">Message Review</option>
            <option value="writing_assistance">Writing Assistance</option>
            <option value="legal_review">Legal Review</option>
            <option value="salary_negotiation">Salary Negotiation</option>
          </select>
        </label>
      </div>

      <div className="stored-communications-content">
        {isLoading && (
          <div className="loading-state">
            <LoadingSpinner size="medium" />
            <p>Loading stored communications...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>⚠️ {error}</p>
            <button onClick={loadCommunications}>Retry</button>
          </div>
        )}

        {!isLoading && !error && communications.length === 0 && (
          <div className="empty-state">
            <p>No stored communications found.</p>
            {application && (
              <p>Communications stored for this application will appear here.</p>
            )}
          </div>
        )}

        {!isLoading && !error && communications.length > 0 && (
          <div className="communications-list">
            {communications.map((comm) => (
              <div key={comm.session_id} className="communication-item">
                <div className="communication-item-header">
                  <h4>{getActionTypeLabel(comm.action_type)}</h4>
                  <span className="communication-date">{formatDate(comm.created_at)}</span>
                </div>
                <div className="communication-item-preview">
                  <p>{getPreviewText(comm).substring(0, 200)}...</p>
                </div>
                {comm.job_application_id && (
                  <div className="communication-item-meta">
                    <span>Linked to Application: {comm.job_application_id}</span>
                    {comm.quality_tier && <span>Quality: {comm.quality_tier === 'premium' ? 'Premium' : 'Standard'}</span>}
                  </div>
                )}
                <div className="communication-item-actions">
                  <button
                    className="view-button"
                    onClick={() => handleView(comm)}
                  >
                    View Full Communication
                  </button>
                  {onEditCommunication && canEditCommunication(comm) && (
                    <button
                      className="edit-button"
                      onClick={() => handleEdit(comm)}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(comm)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <MessageBox {...messageBox} />
    </div>
  )
}
